/* eslint-disable react-hooks/exhaustive-deps */
// libs
import React, { memo, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';

// global components
import Logo from 'shared/components/Logo';

// components
import HeaderApi from 'shared/components/Header/HeaderApi';
import Search from 'shared/components/GlobalSearch/Mobile';
import Cart from './components/Cart';
import Account from './components/Account';
import AccountsNudge from 'shared/components/AccountsNudge';
import Placeholder from './components/Placeholder';
import Wishlist from './components/Wishlist';
import SkipToMainContent from './components/SkipToMainContent';
import TitlePromotionalBanner from '../TitlePromotionalBanner';
import Searchbar from 'shared/components/Header/MobileHeader/components/Searchbar';

// contexts
import { useMobileActionContext } from 'shared/components/Header/MobileAction/Context';
import { useDomainConfig } from 'shared/domainConfig/context/context';

// hooks
import { useKeyboardClick } from 'shared/hooks/useKeyboardClick';

// constants
import { HEADER_LOGO_ALT_TEXT, HOME_URI, MEGA_MENU_V2 } from 'shared/constants/uri';
import { NAV_STATES, SHOW_SEARCH_QUERY_PARAM, SEARCH_QUERY_PAGE_TEXT, ARIA_LABELS } from './constants';
import {
  LOGO_TYPE,
  TAB_INDEX,
  SEARCH_BAR_EXPT_NAME,
  SEARCH_BAR_ELIGIBLE_VARIANT,
} from 'shared/constants';

// helpers
import { canGoBack } from 'shared/helpers/history';
import { HISTORY_KEY } from 'shared/components/SmartProductJourney/constants';
import { log } from 'shared/helpers/newRelic';
import { getWebAppType } from 'shared/helpers/deviceType/webAppType';
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';
import { getStoreUrl, DEFAULT_STORE } from 'shared/helpers/storeUrl';

// styles
import {
  ActionButton,
  Content,
  HeaderContainer,
  IObserver,
  LogoAnchor,
  SearchButton,
} from './styles/header';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// analytics
import { trackBackButtonClicked, trackHeaderSearchClick } from 'shared/components/Header/analytics';

// defs
import { IHeaderProps } from './types';

// color
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// constants
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';
import { WEB_APP_TYPE, SEARCH_ICON_SIZE } from 'shared/constants';

// selector
import { getCustomerId, getStoreId } from 'shared/store/app/selectors';

let timer: number | null = null;

function Header({
  showHamburger = true,
  showBack = false,
  showLogo = true,
  showSearch = true,
  showCart = true,
  headerMerge = false,
  showBottomBorder = true,
  MobileActions,
  HeaderContent,
  showLogoOnLeft = false,
  showWishlist = true,
  newSearchBarPDP = false,
}: IHeaderProps) {
  // Get icon components from unified system
  const SearchIcon = useStoreIcon(IconName.SEARCH);
  const HamburgerMenuIcon = useStoreIcon(IconName.HAMBURGER_MENU);
  const ArrowLeftIcon = useStoreIcon(IconName.ARROW_LEFT);
  const storeId = useSelector(getStoreId);

  const [headerFadeIn, setHeaderFadeIn] = useState(false);
  const [scrolledOnce, setScrolledOnce] = useState(false);
  const customerId = useSelector(getCustomerId);
  const { setShowSearchCallback } = useMobileActionContext();
  const { SHOW_HEADER_ACCOUNT } = useDomainConfig();
  const isHamburgerMenuEnabled = useRemoteConfig('showHamburgerMenu') && showHamburger;
  const history = useHistory();

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const isMobileApp = searchParams.get('src') === 'app';
  const isRewardsPageEnabled = useRemoteConfig('rewardsPageEnabled') || false;

  const { isEligible, variant: isPdpSearchBarVariant = '' } =
    getEligibleVariantName(SEARCH_BAR_EXPT_NAME);
  const pdpSearchBarEnabled =
    newSearchBarPDP && isEligible && isPdpSearchBarVariant === SEARCH_BAR_ELIGIBLE_VARIANT;

  const appFinishCallback = () => {
    const type = getWebAppType();
    try {
      if (WEB_APP_TYPE.ANDROID === type) {
        (window as any).AndroidFunction.finish();
      } else if (WEB_APP_TYPE.IOS === type) {
        window.webkit.messageHandlers.finish.postMessage('finish');
      }
    } catch (error) {
      log({
        errTitle: 'Error in appFinishCallback',
        error: error,
        tags: { [TAG_IDENTIFIER.MODULE]: 'App Finish Callback' },
      });
    }
  };

  const goBack = () => {
    trackBackButtonClicked();
    if (isMobileApp) {
      appFinishCallback();
    } else if (canGoBack() || (history.location.state?.[HISTORY_KEY] && history.location.state?.productJourneyPDP)) {
      window.history.back();
    } else {
      if (storeId !== DEFAULT_STORE) {
        sessionStorage.setItem('store_switched_via_toggle', storeId);
      }
      window.location.href = getStoreUrl(HOME_URI);
    }
  };

  // Clean browser history stack in case user reloaded when on MENU
  // Order of clearing matters

  useEffect(() => {
    if (history.location.state !== undefined) {
      if (history.location.state[NAV_STATES.CATEGORY] !== undefined) {
        history.goBack();
      }
      if (history.location.state[NAV_STATES.MENU] !== undefined) {
        history.goBack();
      }
      if (history.location.state[NAV_STATES.SEARCH] !== undefined) {
        history.goBack();
      }
    }
  }, [history]);

  useEffect(() => {
    if (headerMerge) {
      setHeaderFadeIn(false);
    }
  }, [headerMerge]);

  const showMenu = () => {
    history.push(MEGA_MENU_V2);
  };
  const showSearchPage = () => {
    trackHeaderSearchClick(SEARCH_QUERY_PAGE_TEXT, SEARCH_QUERY_PAGE_TEXT, customerId);
    searchParams.set(SHOW_SEARCH_QUERY_PARAM, `1`);
    history.push({
      pathname: location.pathname,
      search: `${searchParams.toString()}`,
      state: {
        showSearch: true,
      },
    });
  };
  const hideSearchPage = () => {
    if (history.location.state && history.location.state.showSearch) {
      history.goBack();
      return;
    }
    searchParams.delete(SHOW_SEARCH_QUERY_PARAM);
    history.push({
      pathname: location.pathname,
      search: `${searchParams.toString()}`,
    });
  };
  useEffect(() => {
    setShowSearchCallback(showSearchPage);
  }, [setShowSearchCallback]);

  const onHidden = () => {
    timer !== null && window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      setHeaderFadeIn(true);
      setScrolledOnce(true);
    }, 200);
  };
  const onVisible = () => {
    timer !== null && window.clearTimeout(timer);
    setHeaderFadeIn(false);
  };
  const logoClick = (e) => {
    e.preventDefault();

    window.location.href = getStoreUrl('/');
  };
  const headerClassName = (
    headerMerge
      ? [
        headerFadeIn && 'merge-scrolled',
        headerFadeIn === false && 'merge',
        headerFadeIn === false && scrolledOnce && 'animate',
        // header is merged - dont show bottom border
        headerFadeIn && showBottomBorder && 'show-border',
      ]
      : [showBottomBorder && headerFadeIn && 'show-border']
  )
    .filter(Boolean)
    .join(' ');

  const canShowSearchPage = searchParams.get(SHOW_SEARCH_QUERY_PARAM) === '1';

  const menuKeyboardProps = useKeyboardClick(showMenu);
  const backKeyboardProps = useKeyboardClick(goBack);
  const searchKeyboardProps = useKeyboardClick(showSearchPage);

  return (
    <>
      <HeaderApi shouldCallMenuApi={false} placeholder={<Placeholder />} storeId={storeId}>
        {(headerMerge || showBottomBorder) && (
          <IObserver onVisible={onVisible} onHidden={onHidden} />
        )}
        {isRewardsPageEnabled &&
          !location?.search.includes('showsearch=1') &&
          location.pathname !== '/flx-summary' && <TitlePromotionalBanner />}
        <HeaderContainer
          className={headerClassName}
          isRewardsProgram={
            isRewardsPageEnabled &&
            location?.search !== '?showsearch=1' &&
            location.pathname !== '/flx-summary' &&
            location.pathname !== '/my-profile'
          }
        >
          <SkipToMainContent />
          <Content pdpSearchBarEnabled={pdpSearchBarEnabled}>
            {isHamburgerMenuEnabled && (
              <ActionButton
                data-cy="menu"
                {...menuKeyboardProps}
                data-at="hamburger-button"
                aria-label={ARIA_LABELS.OPEN_MENU}
              >
                <HamburgerMenuIcon />
              </ActionButton>
            )}
            {showBack && (
              <ActionButton
                {...backKeyboardProps}
                className="back-button"
                aria-label={ARIA_LABELS.GO_BACK}
              >
                <ArrowLeftIcon />
              </ActionButton>
            )}
            {HeaderContent !== undefined && HeaderContent}
            {showLogo && (
              <LogoAnchor
                onClick={logoClick}
                showLogoOnLeft={showLogoOnLeft || storeId === 'luxe'}
                aria-label={HEADER_LOGO_ALT_TEXT}
                data-at="logo-button"
                href={getStoreUrl(HOME_URI)}
                pdpSearchBarEnabled={pdpSearchBarEnabled}
              >
                <Logo
                  showLogoOnLeft={showLogoOnLeft}
                  type={LOGO_TYPE.MWEB}
                  pdpSearchBarEnabled={pdpSearchBarEnabled}
                />
              </LogoAnchor>
            )}
            {showSearch && pdpSearchBarEnabled && (
              <Searchbar onClick={showSearchPage} tabIndex={TAB_INDEX.ACTION_INVISIBILITY} />
            )}
            <div className="actions">
              {showSearch && !pdpSearchBarEnabled && (
                <SearchButton
                  {...searchKeyboardProps}
                  aria-label={ARIA_LABELS.SEARCH_PRODUCTS}
                  data-at="search-btn"
                >
                  <SearchIcon size={SEARCH_ICON_SIZE} aria-hidden="true" />
                </SearchButton>
              )}

              {MobileActions !== undefined && !pdpSearchBarEnabled && MobileActions}
              {showWishlist && <Wishlist />}

              {showCart && <Cart />}
              {SHOW_HEADER_ACCOUNT && location.pathname === '/' && (
                <AccountsNudge>
                  <Account />
                </AccountsNudge>
              )}
            </div>
          </Content>
        </HeaderContainer>
        {canShowSearchPage && <Search onHide={hideSearchPage} navState={NAV_STATES.SEARCH} />}
      </HeaderApi>
    </>
  );
}

export default memo(Header);
